<template>
  <div class="table-responsive ">
    <table class="table table-hover table-striped">
      <thead>
      <tr>
        <th rowspan="2">
          <input type="checkbox" class="form-check-input" v-model="selectAll" @change="selectAllRows">
        </th>
        <th rowspan="2" @click="sortBy('pdb')">
          RCSB
          <i v-if="sortProp === 'pdb'"
             :class="[sortDir === 'asc' ? 'bi bi-caret-up-fill' : 'bi bi-caret-down-fill']"></i>
        </th>
        <th rowspan="2">
          View
        </th>
        <th rowspan="2">
          Download
        </th>
        <th rowspan="2">
          Description
        </th>
        <th rowspan="2" @click="sortBy('pubmedId')">
          Pubmed
          <i v-if="sortProp === 'pubmedId'"
             :class="[sortDir === 'asc' ? 'bi bi-caret-up-fill' : 'bi bi-caret-down-fill']"></i>
        </th>
        <th>Tags</th>
      </tr>
      <tr>
        <th v-for="col in tagFamilies" :key="col.label">
          {{ col.label }}
        </th>
      </tr>
      </thead>
      <tbody>
      <tr v-for="(row, index) in displayedRows" :key="row.id" @click="selectRow(index)">
        <td>
          <input type="checkbox" class="form-check-input" v-model="row.isSelected">
        </td>
        <td>
          <a :href="row.rcsbLink" @click.stop="onLinkClick">{{ row.pdb }}</a>
        </td>
        <td>
          <a :href="'/structure?structureId=' + row.id" @click.stop="onLinkClick">
            3D
          </a>
        </td>
        <td>
          <a :href="row.plicLink" target="_blank" @click.stop="onLinkClick">Download</a>
        </td>
        <td>
          {{ row.description }}
        </td>
        <td>
          <a :href="row.pubmedLink" @click.stop="onLinkClick">{{ row.pubmedId }}</a>
        </td>
        <td v-for="col in tagFamilies" :key="col.field">
                    <span :class="(tag === searchTag) ? 'badge me-1 bg-info' : 'badge me-1 bg-secondary'"
                          v-for="tag in row.tags[col.field]" :key="tag">
                        <a :href="'/structures?tag=' + tag" class="text-white">{{ tag }}</a>
                    </span>
        </td>
      </tr>
      </tbody>
    </table>
    <nav>
      <ul class="pagination"
      >
        <li class="page-item" :class="{ disabled: currentPage === 1 }">
          <button class="page-link" @click="gotoPage(1)">First</button>
        </li>
        <li class="page-item" :class="{ disabled: currentPage === 1 }">
          <button class="page-link" @click="prevPage()">Previous</button>
        </li>
        <li v-for="page in pageCount" :key="page" class="page-item" :class="{ active: page === currentPage }">
          <button class="page-link" v-if="showPage(page)" @click="gotoPage(page)">{{ page }}</button>
        </li>
        <li class="page-item" :class="{ disabled: currentPage === pageCount || pageCount === 0 }">
          <button class="page-link" @click="nextPage()">Next</button>
        </li>
        <li class="page-item" :class="{ disabled: currentPage === pageCount || pageCount === 0 }">
          <button class="page-link" @click="gotoPage(pageCount)">Last</button>
        </li>
      </ul>
    </nav>
    <div class="mb-3 input-group">
      <button :disabled="noSelectedRows" class="btn btn-info" @click="downloadSelected">Download structures
      </button>
    </div>
  </div>
</template>

<script>
import axios from "axios";

export default {
  props: {
    data: {
      type: Array,
      required: true,
    },
    tagFamilies: {
      type: Object,
      required: true,
    },
    perPage: {
      type: Number,
      default: 25,
    },
    searchTag: {
      type: String,
      default: ""
    }
  },
  data() {
    return {
      sortProp: '',
      sortDir: 'asc',
      currentPage: 1,
      selectAll: false
    };
  },
  computed: {
    pageCount() {
      return Math.ceil(this.data.length / this.perPage);
    },
    displayedRows() {
      const start = (this.currentPage - 1) * this.perPage;
      const end = start + this.perPage;

      // Sort the data
      let sortedData = this.data;
      if (this.sortProp) {
        sortedData = sortedData.sort((a, b) => {
          const propA = a[this.sortProp];
          const propB = b[this.sortProp];

          if (propA < propB) return this.sortDir === 'asc' ? -1 : 1;
          if (propA > propB) return this.sortDir === 'asc' ? 1 : -1;
          return 0;
        });
      }

      return sortedData.slice(start, end);
    },
    noSelectedRows() {
      return this.data.filter((row) => {
        return row.isSelected;
      }).length === 0;
    }
  },
  methods: {
    sortBy(prop) {
      if (this.sortProp === prop) {
        this.sortDir = this.sortDir === 'asc' ? 'desc' : 'asc';
      } else {
        this.sortProp = prop;
        this.sortDir = 'asc';
      }
      this.currentPage = 1;
    },
    prevPage() {
      if (this.currentPage > 1) {
        this.currentPage--;
      }
    },
    nextPage() {
      if (this.currentPage < this.pageCount) {
        this.currentPage++;
      }
    },
    gotoPage(page) {
      this.currentPage = page;
    },
    showPage(page) {
      return Math.abs(page - this.currentPage) <= 2;
    },
    selectAllRows() {
      const newValue = this.selectAll;
      this.data.forEach((row) => {
        row.isSelected = newValue;
      });
    },
    selectRow(index) {
      this.data[index].isSelected = !this.data[index].isSelected;
    },
    onLinkClick(event) {
      event.stopPropagation();
    },
    downloadSelected() {
      const now = new Date();
      const filename = `plic_${now.getFullYear()}-${now.getMonth() + 1}-${now.getDate()}_${now.getHours()}-${now.getMinutes()}-${now.getSeconds()}.zip`;
      const bodyFormData = new FormData();
      const structureIds = this.data.filter((row) => {
        return row.isSelected;
      }).map((row) => {
        return row.id;
      });
      bodyFormData.append('structureIds', structureIds);
      axios.post('/api/structures/download_multiple', bodyFormData, {responseType: 'blob'}).then(response => {
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', filename);
        document.body.appendChild(link);
        link.click();
      });
    }
  },
};
</script>
